import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../trpc.js';
import { useToast } from '../ui/Toast.js';
import { invalidateFundRows } from '../grid/invalidateFundRows.js';

const POLL_MS = 2000;

export interface FundAffinityRefresh {
  start: () => void;
  /** While a refresh is under way: a line the pane shows. Null otherwise. */
  progress: string | null;
  busy: boolean;
}

/**
 * "Refresh from Affinity" on one fund: queue its run, follow it, and when it
 * finishes reload what the pane shows of the fund and its people. The run is
 * told apart from earlier ones by its id: whatever this fund's latest run was
 * when the button was pressed is not the one being waited for.
 */
export function useFundAffinityRefresh(fundId: string): FundAffinityRefresh {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  /** The fund's latest run before this refresh; undefined when none is under way. */
  const [before, setBefore] = useState<string | null | undefined>(undefined);

  // A different fund opened in the same pane is a different question.
  useEffect(() => setBefore(undefined), [fundId]);

  const following = before !== undefined;
  const status = useQuery(
    trpc.affinity.fundSync.queryOptions(
      { fundId },
      { enabled: following, refetchInterval: following ? POLL_MS : false },
    ),
  );

  const refresh = useMutation(
    trpc.affinity.refreshFund.mutationOptions({
      onSuccess: (answer) => setBefore(answer.run?.id ?? null),
      onError: (error) => toast(error.message),
    }),
  );

  const run = status.data?.run ?? null;
  const ours = following && run !== null && run.id !== before ? run : null;
  const finished = ours !== null && (ours.status === 'done' || ours.status === 'failed');

  useEffect(() => {
    if (!finished || ours === null) return;
    setBefore(undefined);
    invalidateFundRows(queryClient, trpc);
    void queryClient.invalidateQueries({ queryKey: trpc.investors.list.queryKey({ fundId }) });
    if (ours.status === 'failed') {
      toast(`Affinity refresh stopped: ${ours.error ?? 'no reason given'}`);
    } else {
      toast(
        `From Affinity: ${plural(ours.investorsCreated, 'person', 'people')} added, ` +
          `${plural(ours.relationshipsWritten, 'relationship', 'relationships')} updated ` +
          `(${ours.callsMade} calls).`,
        { tone: 'info' },
      );
    }
  }, [finished, ours, fundId, queryClient, trpc, toast]);

  let progress: string | null = null;
  if (following) {
    if (ours !== null) progress = 'Reading Affinity…';
    else if (status.data?.otherRunActive === true) progress = 'Waiting for another Affinity run…';
    else progress = 'Affinity refresh queued…';
  }

  return {
    start: () => refresh.mutate({ fundId }),
    progress,
    busy: following || refresh.isPending,
  };
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}
